/**
 * AuthoringRuns — ADR-177 D3, the fleet's authoring runs, newest first.
 *
 * The list a run has never had. Before this, a run was one jsonb column keyed
 * (agent, workflow, operator) and overwritten in place, so the only listable trace of a
 * six-phase run was six untitled chat rows — which is why the authoring rail's
 * observability ended up living inside a chat drawer.
 *
 * Read fleet-wide on purpose (ADR-137 D8's asymmetry): everyone reads the record and the
 * operator column says whose run it was. A history only its author could read would be
 * useless for the thing it exists for, which is a second person working out why a stage
 * keeps refusing.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api, type AuthoringRunSummary } from '../api/client'
import { AttributionCoveragePanel } from '../components/AttributionCoverage'
import { duration, elapsedS } from '../components/RunTree'

export function AuthoringRuns() {
  // SCOPED BY DEFAULT WHEN IT WAS OPENED FROM A WORKFLOW.
  //
  // The first version listed the whole fleet and had no route home, so an operator who
  // clicked through from an editor lost their place: "I can't get back to the fleet
  // editor I was looking at without clicking into fleet editor, finding the workflow,
  // opening, selecting edit." The runs of the workflow you are editing are the ones you
  // want; everything else is one click away, not the default.
  const [params] = useSearchParams()
  const agent = params.get('agent') || ''
  const workflow = params.get('workflow') || ''
  const scoped = agent !== '' && workflow !== ''

  const [runs, setRuns] = useState<AuthoringRunSummary[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'not_configured'>('loading')
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    try {
      const r = await api.authoringRunList({ agent, workflow, limit: 100 })
      if (r.not_configured) { setState('not_configured'); return }
      setRuns(r.runs || [])
      setState('ready')
    } catch (e) {
      // NOT an empty list. A read that failed and a fleet that has authored nothing look
      // identical if you render both as "no runs", and only one of them is worth acting
      // on.
      setErr(e instanceof Error ? e.message : 'the run history could not be read')
      setState('error')
    }
  }, [agent, workflow])

  useEffect(() => { void load() }, [load])

  return (
    <div className="mx-auto max-w-5xl space-y-3 p-4">
      {/* THE WAY BACK, first thing on the page. A surface reached from an editor that
          cannot return to it is a dead end, and the operator's own route back was six
          clicks. */}
      {scoped && (
        <Link
          to={`/fleet-editor/agents/${encodeURIComponent(agent)}/workflows/${encodeURIComponent(workflow)}`}
          className="text-[11px] text-indigo-700 hover:underline">
          ← back to {agent}/{workflow}
        </Link>
      )}
      <div>
        <h1 className="text-base font-semibold text-gray-900">
          {scoped ? <>Authoring runs — <span className="font-mono text-sm">{agent}/{workflow}</span></> : 'Authoring runs'}
        </h1>
        <p className="text-[11px] text-gray-600">
          Every authored change and the phases it moved through. A run is kept when the
          next one starts.
          {scoped && <> <Link to="/fleet-editor/runs" className="text-indigo-700 hover:underline">
            Show every workflow&apos;s runs
          </Link>.</>}
        </p>
      </div>

      {/* ADR-183 Phase 5b — on the UNSCOPED view only. The report is fleet-wide and
          spans five tables; showing it beside one workflow's runs would invite reading
          it as that workflow's coverage. Its own read failure never affects the list
          below (ADR-167 D4's rule: a panel degrades to itself, not to the page). */}
      {!scoped && <AttributionCoveragePanel />}

      {state === 'loading' && <div className="text-sm text-gray-600">Reading the run history…</div>}

      {state === 'not_configured' && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This Mission Control has no session store, so authoring runs are not archived.
          Runs still happen — nothing is recording them here.
        </div>
      )}

      {state === 'error' && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>Could not read the run history.</strong> {err}
          <button type="button" onClick={() => void load()}
            className="ml-2 rounded border border-red-300 bg-white px-2 py-0.5 hover:bg-red-100">
            Retry
          </button>
        </div>
      )}

      {state === 'ready' && runs.length === 0 && (
        <div className="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
          {scoped
            ? <>No authoring runs recorded for <span className="font-mono">{agent}/{workflow}</span> yet.</>
            : 'No authoring runs have been recorded on this fleet yet.'}
        </div>
      )}

      {state === 'ready' && runs.length > 0 && (
        <div className="divide-y divide-gray-200 rounded border border-gray-200 bg-white">
          {runs.map(r => <Row key={r.id} r={r} />)}
        </div>
      )}
    </div>
  )
}

function Row({ r }: { r: AuthoringRunSummary }) {
  const total = elapsedS(r.opened_at, r.closed_at)
  // ADR-178 D3 — THE RUN POINTS AT ITS OWN APPROVAL.
  //
  // A run that reaches `done` proposes by itself, and this list could say it
  // finished while saying nothing about what it produced. Reported on spartan
  // 2026-09-17: PR #1179 was "nowhere to be found ... no means of being approved
  // because it didn't follow the normal path". The Approve & Deploy surface existed
  // the whole time; nothing on the run linked to it.
  //
  // OUTSIDE THE ROW'S OWN Link, because a link inside a link is invalid and the
  // browser picks for you. The row still opens the run; this opens the decision.
  const approve = r.improvement_id ? `/improvements/${encodeURIComponent(r.improvement_id)}/pr` : null
  return (
    <div className="px-3 py-2 hover:bg-gray-50">
    <Link to={`/fleet-editor/runs/${encodeURIComponent(r.id)}`}
      className="block">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-[13px] text-gray-900">{r.task || 'Untitled run'}</span>
        <Outcome r={r} />
      </div>
      <div className="text-[11px] text-gray-600">
        <span className="font-mono">{r.agent}/{r.workflow}</span>
        {' · '}{r.attempts} attempt{r.attempts === 1 ? '' : 's'}
        {/* THE REFUSAL COUNT IS ON THE ROW. It is what a reader scans this list for, and
            it has never been countable without opening every run. */}
        {r.refusals > 0 && <span className="text-red-700"> · {r.refusals} refused</span>}
        {' · '}{r.closed_at ? duration(total) : 'still going'}
        {' · '}{new Date(r.opened_at).toLocaleString()}
        {' · '}{r.operator}
      </div>
    </Link>
    {approve && (
      <div className="mt-1 text-[11px]">
        <Link to={approve} className="font-medium text-emerald-800 underline decoration-emerald-400 hover:decoration-emerald-700">
          {r.pr_number ? `Review PR #${r.pr_number} — approve or decline` : 'Review the change — approve or decline'}
        </Link>
      </div>
    )}
    </div>
  )
}

function Outcome({ r }: { r: AuthoringRunSummary }) {
  // An in-flight run reports the PHASE it is on, never elapsed time (ADR-167 D2): at
  // 1016s "elapsed" cannot separate working from looping from stuck.
  if (!r.outcome || r.outcome === 'in-progress') {
    return (
      <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] text-indigo-800">
        {r.phase ? `at ${String(r.phase).replace(/[-_]/g, ' ')}` : 'in flight'}
      </span>
    )
  }
  // `proposed` IS THE BEST OUTCOME A RUN HAS, and it fell through to the neutral default —
  // the same grey this component gives an outcome it has never heard of. Measured on spartan
  // 2026-09-17: 20260917T115852Z-c2d2b1 reached `done` and opened PR #1179, and the one run
  // of six that produced something reviewable was the least legible row on the page.
  //
  // `propose-failed` is styled explicitly, and AGAINST it, because ADR-178 D3's whole point
  // is that "finished" and "finished with an artifact" are different facts. Leaving it on the
  // grey default would report a run that ended with nothing to review as merely unremarkable.
  const cls =
    r.outcome === 'proposed' ? 'bg-emerald-100 font-medium text-emerald-900'
      : r.outcome === 'complete' ? 'bg-emerald-100 text-emerald-800'
        : r.outcome === 'refused' ? 'bg-red-100 text-red-800'
          : r.outcome === 'propose-failed' ? 'bg-red-100 text-red-800'
            : r.outcome === 'awaiting-decision' ? 'bg-amber-100 text-amber-900'
              : 'bg-gray-100 text-gray-700'
  return <span className={`rounded px-2 py-0.5 text-[10px] ${cls}`}>{r.outcome}</span>
}
